/* Mobile Teacher PWA — main entry with bottom-tab navigation.
 * Path: /m/teacher/(home|notes|availability|profile)
 * Role: teacher_user (PIN 6 chiffres)
 */
import { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { mobileTeacherApi } from '../../lib/api';
import { queueLength, startAutoFlush } from '../../lib/offlineQueue';
import { toast } from 'sonner';
import {
  House, ChalkboardTeacher, CalendarCheck, User,
  SignOut, Warning, WifiSlash, CloudArrowUp, ClipboardText,
} from '@phosphor-icons/react';

import TeacherHome from './teacher/TeacherHome';
import TeacherNotes from './teacher/TeacherNotes';
import TeacherAvailability from './teacher/TeacherAvailability';
import TeacherProfile from './teacher/TeacherProfile';
import TeacherTimetable from './teacher/TeacherTimetable';
import TeacherRollcall from './teacher/TeacherRollcall';

const TABS = [
  { path: 'home', label: 'Accueil', icon: House },
  { path: 'rollcall', label: 'Appel', icon: ClipboardText },
  { path: 'notes', label: 'Notes', icon: ChalkboardTeacher },
  { path: 'availability', label: 'RDV', icon: CalendarCheck },
  { path: 'profile', label: 'Profil', icon: User },
];

const MobileTeacherApp = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [notif, setNotif] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingQueue, setPendingQueue] = useState(queueLength());

  const currentTab = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    return parts[2] || 'home';
  }, [location.pathname]);

  // Load /me + notifications count on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [meRes, notifRes] = await Promise.all([
          mobileTeacherApi.me(),
          mobileTeacherApi.notificationsCount(),
        ]);
        if (!mounted) return;
        setMe(meRes.data);
        setNotif(notifRes.data?.pending_appointments || 0);
      } catch (e) {
        if (!mounted) return;
        setError(e.response?.data?.detail || 'Erreur de chargement');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Poll notif count every 60s
  useEffect(() => {
    const it = setInterval(async () => {
      try {
        const r = await mobileTeacherApi.notificationsCount();
        setNotif(r.data?.pending_appointments || 0);
      } catch { /* silent */ }
    }, 60000);
    return () => clearInterval(it);
  }, []);

  // Online/offline detection + auto-flush queue
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    const onQueueChange = () => setPendingQueue(queueLength());
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('gs.offlineQueueChanged', onQueueChange);
    const unsub = startAutoFlush((res) => {
      onQueueChange();
      if (res.ok > 0) toast.success(`✓ ${res.ok} note(s) synchronisée(s)`);
    });
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('gs.offlineQueueChanged', onQueueChange);
      if (unsub) unsub();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Déconnecté');
    } catch { /* noop */ }
    navigate('/m/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" />
      </div>
    );
  }

  if (error || !me) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] p-6 text-center">
        <Warning size={40} className="text-red-500 mb-3" weight="duotone" />
        <p className="text-sm text-[#64748B] mb-4">{error}</p>
        <button
          onClick={handleLogout}
          className="text-sm text-[#0052FF] underline"
          data-testid="teacher-logout-err"
        >
          Se déconnecter
        </button>
      </div>
    );
  }

  const showBanner = !isOnline || pendingQueue > 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="mobile-teacher-app">
      {/* Offline / queue banner */}
      {showBanner && (
        <div
          className={`sticky top-0 z-40 px-4 py-1.5 text-xs font-medium text-white flex items-center justify-center gap-2 ${
            !isOnline ? 'bg-red-500' : 'bg-amber-500'
          }`}
          data-testid="offline-banner"
        >
          {!isOnline ? (
            <>
              <WifiSlash size={14} weight="bold" />
              Mode hors ligne — vos actions seront synchronisées à la reconnexion
            </>
          ) : (
            <>
              <CloudArrowUp size={14} weight="bold" />
              {pendingQueue} enregistrement{pendingQueue > 1 ? 's' : ''} en attente de synchronisation…
            </>
          )}
        </div>
      )}

      {/* Sticky top bar */}
      <header className="sticky z-30 bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center gap-3" style={{ top: showBanner ? 26 : 0 }}>
        {me.photo_base64 ? (
          <img
            src={me.photo_base64}
            alt=""
            className="h-10 w-10 rounded-full object-cover border border-[#E2E8F0]"
          />
        ) : (
          <div className="h-10 w-10 rounded-full bg-[#0052FF] text-white font-outfit flex items-center justify-center">
            {(me.first_name?.[0] || '') + (me.last_name?.[0] || '')}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-outfit font-bold text-sm truncate" data-testid="teacher-name">
            {me.first_name} {me.last_name}
          </div>
          <div className="text-xs text-[#64748B] truncate">
            {me.subject || me.function || 'Enseignant(e)'}
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="p-2 rounded-full hover:bg-[#F1F5F9] text-[#64748B]"
          title="Se déconnecter"
          data-testid="teacher-logout"
        >
          <SignOut size={20} />
        </button>
      </header>

      {/* Content area */}
      <main className="flex-1 overflow-y-auto pb-24">
        <Routes>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<TeacherHome me={me} notif={notif} onNavigate={(p) => navigate(`/m/teacher/${p}`)} />} />
          <Route path="rollcall" element={<TeacherRollcall me={me} />} />
          <Route path="notes" element={<TeacherNotes me={me} />} />
          <Route path="timetable" element={<TeacherTimetable />} />
          <Route path="availability" element={<TeacherAvailability me={me} onNotifChange={setNotif} />} />
          <Route path="profile" element={<TeacherProfile me={me} />} />
          <Route path="*" element={<Navigate to="home" replace />} />
        </Routes>
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] shadow-[0_-2px_10px_rgba(0,0,0,0.04)] z-40">
        <ul className="grid grid-cols-5">
          {TABS.map(tab => {
            const active = currentTab === tab.path;
            const showBadge = tab.path === 'availability' && notif > 0;
            return (
              <li key={tab.path}>
                <button
                  onClick={() => navigate(`/m/teacher/${tab.path}`)}
                  data-testid={`tab-${tab.path}`}
                  className={`w-full py-2.5 flex flex-col items-center gap-0.5 transition-colors relative ${
                    active ? 'text-[#0052FF]' : 'text-[#94A3B8] hover:text-[#64748B]'
                  }`}
                >
                  <tab.icon size={22} weight={active ? 'duotone' : 'regular'} />
                  <span className={`text-[10px] font-medium ${active ? 'font-outfit font-semibold' : ''}`}>
                    {tab.label}
                  </span>
                  {showBadge && (
                    <span
                      className="absolute top-1.5 right-6 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
                      data-testid="tab-availability-badge"
                    >
                      {notif > 9 ? '9+' : notif}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default MobileTeacherApp;
